import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { loginPath } from '../auth'
import { loadConfig } from '../config'

const TIERS = [
  { tier: 'Free', price: '£0', decks: '1 deck', images: '20 images · 50 MB' },
  { tier: 'Starter', price: '£3.00', decks: '5 decks', images: '100 images · 250 MB' },
  { tier: 'Standard', price: '£7.50', decks: '10 decks', images: '300 images · 750 MB' },
  { tier: 'Unlimited', price: '£15.00', decks: 'Unlimited decks', images: '2000 images · 5 GB' },
]

/** The only page an unauthenticated visitor sees (spec §8.1). */
export default function About({ returnTo }: { returnTo: string }) {
  const [abuseEmail, setAbuseEmail] = useState<string | null>(null)

  useEffect(() => {
    loadConfig()
      .then((config) => setAbuseEmail(config.abuseEmail ?? null))
      .catch(() => setAbuseEmail(null))
  }, [])

  return (
    <>
      <h1>Flashcards for structured revision</h1>
      <p>
        Write a deck as JSON or YAML, upload it, and revise it in <strong>learn</strong> mode
        or under test conditions with a score you can track over time. Everything the web
        app does is a documented API call, so an agent can do it too.
      </p>

      <p className="row">
        <Link className="button primary" to={loginPath({ returnTo })}>
          Log in or sign up
        </Link>
        <a className="button" href="/api/docs">
          API documentation
        </a>
      </p>

      <h2>Plans</h2>
      <table className="pricing">
        <thead>
          <tr>
            <th>Plan</th>
            <th>Per month</th>
            <th>Decks</th>
            <th>Images</th>
          </tr>
        </thead>
        <tbody>
          {TIERS.map((row) => (
            <tr key={row.tier}>
              <td className="tier">{row.tier}</td>
              <td>{row.price}</td>
              <td>{row.decks}</td>
              <td>{row.images}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted">
        If you cancel or move to a smaller plan, nothing is deleted: decks beyond the new
        limit are locked, stay visible, and can be restored once you are back inside your
        allowance.
      </p>

      <h2>Acceptable use</h2>
      <p>
        Flightdeck is for revision material. Decks and images are private to the account
        that uploads them, and you may only upload content you have the right to use. Do
        not upload sexually explicit material, anything that sexualises minors, hate
        symbols or propaganda, or anything else that is illegal where you or we are.
      </p>
      <p>
        Uploaded images are checked automatically and rejected if they breach this policy.
        Accounts that break these rules are suspended, their content is removed, and
        illegal material is preserved and reported to the relevant authorities.
      </p>
      {abuseEmail ? (
        <p>
          To report abuse, including an image link you have been sent, email{' '}
          <a href={`mailto:${abuseEmail}`}>{abuseEmail}</a> with the link.
        </p>
      ) : null}
    </>
  )
}
